// RS Quick Add — homepage product carousel
// Handles inline variant picker + AJAX cart add for .rs-pc__quick-add buttons.

(function () {
  'use strict';

  // ── Variant JSON parsing ─────────────────────────────────────────────────────
  // Scoped to a host (a .rs-pc__card or [data-rs-qa-host] wrapper) so two
  // consumers rendering the same product on one page don't cross-resolve.
  function parseVariants(host, productId) {
    if (!host) return null;
    var el = host.querySelector('.rs-pc__variants-json[data-for="' + productId + '"]');
    if (!el) return null;
    try { return JSON.parse(el.textContent.trim()); }
    catch (e) { console.error('rs-quick-add: variant JSON parse failed', e); return null; }
  }

  // ── Selection helpers ────────────────────────────────────────────────────────
  // Returns { optionIndex_0based: selectedValue, ... } for all picker rows
  function getSelections(picker) {
    var selections = {};
    picker.querySelectorAll('.rs-pc__picker-row').forEach(function (row) {
      var idx = parseInt(row.dataset.optionIndex) - 1;
      var sel = row.querySelector('[data-selected="true"]');
      if (sel) selections[idx] = sel.dataset.value;
    });
    return selections;
  }

  // Mark each option button as unavailable if no available variant satisfies
  // (this value for this option) + (all other currently selected option values)
  function updateUnavailability(picker, variants) {
    var selections = getSelections(picker);
    picker.querySelectorAll('.rs-pc__picker-row').forEach(function (row) {
      var optIdx = parseInt(row.dataset.optionIndex) - 1;
      row.querySelectorAll('[data-value]').forEach(function (btn) {
        var value = btn.dataset.value;
        var available = variants.some(function (v) {
          if (!v.available) return false;
          if (v.options[optIdx] !== value) return false;
          return Object.keys(selections).every(function (k) {
            var i = parseInt(k);
            return i === optIdx || v.options[i] === selections[k];
          });
        });
        btn.dataset.unavailable = available ? 'false' : 'true';
      });
    });
  }

  // Update "Color: Ivory" label to reflect the currently selected swatch.
  // Scope label lookup to the picker's host card so two cards rendering the
  // same product don't update the wrong card's label.
  function updateColorLabel(picker) {
    var host = picker.closest('.rs-pc__card, [data-rs-qa-host]');
    if (!host) return;
    var labelSpan = host.querySelector('.rs-pc__picker-label-value');
    if (!labelSpan) return;
    var colorRow = picker.querySelector('[data-is-color-row="true"]');
    if (!colorRow) return;
    var selectedSwatch = colorRow.querySelector('.rs-pc__picker-swatch[data-selected="true"]');
    if (selectedSwatch) {
      labelSpan.textContent = (selectedSwatch.getAttribute('aria-label') || selectedSwatch.dataset.value).toUpperCase();
    }
  }

  // Pre-select the options of the chosen variant. Prefers the server-rendered
  // data-default-variant-id (lowest-priced in-stock, alphabetical-by-color
  // tiebreaker — see snippets/rs-product-card.liquid). Falls back to the first
  // available variant, then to the first variant.
  function preselectFirst(picker, variants) {
    var defaultId = picker.dataset.defaultVariantId;
    var first;
    if (defaultId) {
      first = variants.find(function (v) { return String(v.id) === defaultId; });
    }
    if (!first) {
      first = variants.find(function (v) { return v.available; }) || variants[0];
    }
    picker.querySelectorAll('.rs-pc__picker-row').forEach(function (row) {
      var optIdx = parseInt(row.dataset.optionIndex) - 1;
      var target = first.options[optIdx];
      row.querySelectorAll('[data-value]').forEach(function (btn) {
        var isSelected = btn.dataset.value === target;
        btn.dataset.selected = isSelected ? 'true' : 'false';
        if (btn.classList.contains('rs-pc__picker-size')) {
          btn.classList.toggle('rs-label-inverse', isSelected);
        }
      });
    });
    updateColorLabel(picker);
  }

  // Return the variant object whose options match every current selection, or null
  function getSelectedVariant(picker, variants) {
    var selections = getSelections(picker);
    if (!Object.keys(selections).length) return null;
    return variants.find(function (v) {
      return Object.keys(selections).every(function (k) {
        return v.options[parseInt(k)] === selections[k];
      });
    }) || null;
  }

  // ── Live price update ────────────────────────────────────────────────────────
  // Price node lives outside the picker. Walk up to the host wrapper:
  // - .rs-pc__card (carousel / collection / CAB card consumers)
  // - [data-rs-qa-host] (any Quick Add consumer that isn't a card — e.g.
  //   sections/rs-set-feature.liquid editorial moment, where the picker
  //   sits inside a 2-col grid layout rather than a 4:5 product card)
  // Inside that host the price node is .rs-pc__price OR [data-price-target].
  function findPriceNode(picker) {
    var host = picker.closest('.rs-pc__card, [data-rs-qa-host]');
    return host ? host.querySelector('.rs-pc__price, [data-price-target]') : null;
  }

  // The amount lives in an inner [data-rs-price-amount] span; the small
  // currency code (.rs-price-cur, snippets/rs-price-cur.liquid, CAD label
  // 2026-10-17) is its sibling inside the price node. Write and cache the
  // amount node only: setting textContent on the whole price node would
  // delete the code. Falls back to the price node for markup without it.
  function priceAmountNode(priceNode) {
    return priceNode.querySelector('[data-rs-price-amount]') || priceNode;
  }

  // The small "From" above the card price (snippets/rs-product-card.liquid,
  // [data-rs-price-from], Sam 2026-10-17 layout V2) is a sibling of the price
  // node, not inside it, so it is found from the host. It renders only when
  // the product's price varies. Hidden while a variant is selected (that
  // price is exact, not a floor); shown again on reset. No-op when absent.
  function setPriceFromHidden(picker, hide) {
    var host = picker.closest('.rs-pc__card, [data-rs-qa-host]');
    var fromNode = host && host.querySelector('[data-rs-price-from]');
    if (fromNode) fromNode.hidden = hide;
  }

  // ── Price in the Add to Cart button (quick-add `show_price: true`) ───────────
  // Sam 2026-10-15, option C: the sleep trial band's button reads
  // "Add to Cart · $95". The price lives in data-rs-add-price on the button
  // and the label is always rebuilt as ONE text node from it — never a child
  // span, which would lose the class's weight 500 to the universal
  // `* { font-weight: 300 !important }` (rs-tokens.css). Every place that
  // restores the resting label calls this, so a button without the attribute
  // gets exactly the old 'Add to Cart'.
  // Selected variant sold out → 'Sold Out' with no price (price mode only).
  function addBtnLabel(btn) {
    var price = btn.dataset.rsAddPrice;
    if (!price) return 'Add to Cart';
    if (btn.dataset.rsAddSoldOut === 'true') return 'Sold Out';
    return 'Add to Cart · ' + price;
  }

  // Repaint the resting label, but never over Adding… / Added.
  function paintAddBtn(btn) {
    if (btn.dataset.state === 'loading' || btn.dataset.state === 'added') return;
    btn.textContent = addBtnLabel(btn);
  }

  function findPriceAddBtn(picker) {
    return picker.querySelector('.rs-pc__picker-add[data-rs-add-price]');
  }

  // NOTE: findSizeNote() lived here until 2026-09-15. The size note
  // (.rs-pc__price-size) sat under the card price and was synced to the
  // selected size pill. The card stopped rendering it when the title and price
  // moved onto the scrim, so the lookup returned null on every surface and both
  // call sites were dead branches. Removed along with the markup and its CSS.
  // ── Card image swap (per-color) ──────────────────────────────────────────────
  // Parse the color→first-main-image map emitted by snippets/quick-add.liquid
  // (.rs-pc__color-images). Same host-walk as the helpers above so two cards
  // rendering the same product don't cross-read. Cache the parsed object on the
  // host element to avoid re-parsing on every swatch click. Tolerates a missing
  // or empty/{} map (single-color products, or colors with no gallery metafield).
  function findColorImageMap(picker) {
    var host = picker.closest('.rs-pc__card, [data-rs-qa-host]');
    if (!host) return null;
    if (host._rsColorImages !== undefined) return host._rsColorImages;
    var el = host.querySelector('.rs-pc__color-images');
    var map = null;
    if (el) {
      try { map = JSON.parse(el.textContent.trim()); }
      catch (e) { console.error('rs-quick-add: color-image map parse failed', e); map = null; }
    }
    host._rsColorImages = map;
    return map;
  }

  // Parse the color→SECOND-main-image map emitted by snippets/quick-add.liquid
  // (.rs-pc__color-images-alt), used by the card hover crossfade. Mirrors
  // findColorImageMap EXCEPT the cache key: _rsColorImagesAlt, never
  // _rsColorImages — sharing the key would make whichever map parsed first
  // serve both lookups, silently painting the alt image with the primary URL.
  // Colors with fewer than 2 gallery images are omitted from this map by the
  // builder, so a miss here is the normal "no alt image" signal, not an error.
  function findColorImageMapAlt(picker) {
    var host = picker.closest('.rs-pc__card, [data-rs-qa-host]');
    if (!host) return null;
    if (host._rsColorImagesAlt !== undefined) return host._rsColorImagesAlt;
    var el = host.querySelector('.rs-pc__color-images-alt');
    var map = null;
    if (el) {
      try { map = JSON.parse(el.textContent.trim()); }
      catch (e) { console.error('rs-quick-add: alt color-image map parse failed', e); map = null; }
    }
    host._rsColorImagesAlt = map;
    return map;
  }

  // Point an <img> at a new URL. Drops srcset/sizes/width/height so the new
  // src wins and CSS owns sizing — extracted verbatim from swapCardImage's
  // original body so both the primary and the alt swap behave identically.
  function setImgSrc(img, url) {
    img.src = url;
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.removeAttribute('width');
    img.removeAttribute('height');
  }

  // Swap the card's main <img> to the selected color's first gallery image.
  // Graceful no-op when the color has no mapped URL or the card has no
  // [data-rs-variant-image] target — mirrors the PDP gallery's `if (!entry) return`.
  // Drops srcset/sizes/width/height so the new src wins and CSS owns sizing.
  function swapCardImage(picker, colorValue) {
    if (!colorValue) return;
    var key = colorValue.toLowerCase();
    var host = picker.closest('.rs-pc__card, [data-rs-qa-host]');
    // Fallback ancestor for surfaces where the image hook is OUTSIDE the
    // qa-host subtree (e.g. featured-bundle: .rs-cfb__media is a sibling of the
    // body that holds [data-rs-qa-host]). On cards the in-host lookup below
    // already resolves the img, so this never gets used there.
    var wide = host ? (host.closest('.rs-cfb__card, .rs-pc__card') || host.parentElement) : null;

    // ── Primary image ── behaviour unchanged from before the alt was added:
    // no map, no URL for this color, or no target element → no-op.
    var map = findColorImageMap(picker);
    if (map) {
      var url = map[key];
      var img = host ? host.querySelector('[data-rs-variant-image] img') : null;
      if (!img && wide) img = wide.querySelector('[data-rs-variant-image] img');
      if (url && img) setImgSrc(img, url);
    }

    // ── Alt image (hover crossfade) ── independent of the primary by design.
    // A missing map, a color with no second image, or a card rendered without
    // `hover_image: true` (no [data-rs-variant-image-alt] in the DOM) each
    // no-op here and cannot affect the primary swap above, which has already
    // completed. Marker attribute, never position — the alt is the second
    // <img> today but nothing may depend on that.
    var mapAlt = findColorImageMapAlt(picker);
    if (mapAlt) {
      var urlAlt = mapAlt[key];
      var imgAlt = host ? host.querySelector('[data-rs-variant-image-alt]') : null;
      if (!imgAlt && wide) imgAlt = wide.querySelector('[data-rs-variant-image-alt]');
      if (urlAlt && imgAlt) setImgSrc(imgAlt, urlAlt);
    }
  }

  function updateCardPrice(picker, variants) {
    // Price-in-button (trial band). Independent of findPriceNode: the button
    // carries no data-price-target, so a host's own price node is unaffected.
    var addBtn = findPriceAddBtn(picker);
    if (addBtn) {
      if (!addBtn.dataset.rsAddPriceOriginal) {
        addBtn.dataset.rsAddPriceOriginal = addBtn.dataset.rsAddPrice;
      }
      var addVariant = getSelectedVariant(picker, variants);
      if (addVariant) {
        addBtn.dataset.rsAddPrice = addVariant.price_formatted || ('$' + (addVariant.price / 100).toFixed(2));
        addBtn.dataset.rsAddSoldOut = addVariant.available ? 'false' : 'true';
      }
      paintAddBtn(addBtn);
    }

    var priceNode = findPriceNode(picker);
    if (!priceNode) return;
    var amountNode = priceAmountNode(priceNode);
    // Cache the server-rendered price on first touch so we can revert on close.
    if (!priceNode.dataset.priceOriginal) {
      priceNode.dataset.priceOriginal = amountNode.textContent.trim();
    }
    var variant = getSelectedVariant(picker, variants);
    if (!variant) return;
    var formatted = variant.price_formatted || ('$' + (variant.price / 100).toFixed(2));
    amountNode.textContent = formatted;
    setPriceFromHidden(picker, true);

    // Bundle-savings repaint (collection featured-bundle only). Other consumers
    // (carousel, CAB, set-feature) emit no [data-bundle-value-target], so bnode
    // is null and this no-ops. Matches the selected variant's size value against
    // the size→value map carried on the node (position-independent find).
    var host = picker.closest('.rs-pc__card, [data-rs-qa-host]');
    var bnode = host && host.querySelector('[data-bundle-value-target]');
    if (bnode && bnode.dataset.bundleValues) {
      bnode.dataset.orig = bnode.dataset.orig || bnode.textContent;
      try {
        var bmap = JSON.parse(bnode.dataset.bundleValues);
        var bsize = (variant.options || []).find(function (o) { return bmap[o]; });
        if (bsize && bmap[bsize]) bnode.textContent = bmap[bsize];
      } catch (e) { /* malformed map — leave node as-is */ }
    }

    // Bundle-SAVING repaint (featured-bundle badge only).
    //
    // ONE-OFF WIDENED SCOPE — DO NOT COPY THIS AS A PATTERN. Every other
    // lookup in this file scopes to [data-rs-qa-host] per the per-card
    // closest-ancestor rule, and that is still correct for them. The badge
    // cannot use it: it lives in .rs-cfb__media, which is a SIBLING of
    // .rs-cfb__body[data-rs-qa-host], so a host-scoped query can never
    // reach it. Widening the host itself was rejected — it would pull the
    // media anchor into findPriceNode and the variant-image lookups. So we
    // step out to the card for this node and this node only.
    var card = host && host.closest('.rs-cfb__card');
    var snode = card && card.querySelector('[data-bundle-saving-target]');
    if (snode && snode.dataset.bundleSavings) {
      snode.dataset.orig = snode.dataset.orig || snode.textContent;
      try {
        var smap = JSON.parse(snode.dataset.bundleSavings);
        var ssize = (variant.options || []).find(function (o) { return smap[o]; });
        // No entry for this size means the saving is non-positive or was
        // unresolvable; leave the server-rendered text rather than blanking it.
        if (ssize && smap[ssize]) snode.textContent = smap[ssize];
      } catch (e) { /* malformed map — leave node as-is */ }
    }
  }

  function revertCardPrice(picker) {
    // Price-in-button: back to the server-rendered default price. The label
    // itself is repainted by closePicker right after this.
    var addBtn = findPriceAddBtn(picker);
    if (addBtn && addBtn.dataset.rsAddPriceOriginal) {
      addBtn.dataset.rsAddPrice = addBtn.dataset.rsAddPriceOriginal;
      addBtn.dataset.rsAddSoldOut = 'false';
    }
    var priceNode = findPriceNode(picker);
    if (priceNode && priceNode.dataset.priceOriginal) {
      priceAmountNode(priceNode).textContent = priceNode.dataset.priceOriginal;
    }
    setPriceFromHidden(picker, false);
    // Parallel restore for the bundle-savings node (featured-bundle only;
    // no-op elsewhere — no [data-bundle-value-target] to find).
    var bhost = picker.closest('.rs-pc__card, [data-rs-qa-host]');
    var bnode = bhost && bhost.querySelector('[data-bundle-value-target]');
    if (bnode && bnode.dataset.orig) {
      bnode.textContent = bnode.dataset.orig;
    }
    // Parallel restore for the badge saving. Same one-off .rs-cfb__card
    // scope as updateCardPrice — see the note there before reusing it.
    var scard = bhost && bhost.closest('.rs-cfb__card');
    var snode = scard && scard.querySelector('[data-bundle-saving-target]');
    if (snode && snode.dataset.orig) {
      snode.textContent = snode.dataset.orig;
    }
  }

  // ── Picker open / close ──────────────────────────────────────────────────────
  function openPicker(picker, outerBtn, variants) {
    picker.removeAttribute('hidden');
    picker.dataset.open = 'true';
    preselectFirst(picker, variants);
    updateUnavailability(picker, variants);
    updateCardPrice(picker, variants);
    outerBtn.textContent = 'Cancel';
  }

  function closePicker(picker, outerBtn) {
    delete picker.dataset.open;
    outerBtn.textContent = 'Quick Add';
    var addBtn = picker.querySelector('.rs-pc__picker-add');
    if (addBtn) addBtn.dataset.state = '';
    revertCardPrice(picker);
    // After the revert, so a price-in-button label shows the default price.
    // Without data-rs-add-price this is the plain 'Add to Cart', as before.
    if (addBtn) addBtn.textContent = addBtnLabel(addBtn);
  }

  // ── Cart add ─────────────────────────────────────────────────────────────────
  function addToCart(variantId, btn, picker, outerBtn) {
    var origText = btn.textContent;
    // Resting label to restore. The picker's Add to Cart rebuilds it from
    // the CURRENT selection (price-in-button: the price may have changed
    // during the 2-3 s wait). The single-variant direct add passes the outer
    // Quick Add button, which has no picker-add class: keep its own text.
    function restLabel() {
      return btn.classList.contains('rs-pc__picker-add') ? addBtnLabel(btn) : origText;
    }
    btn.dataset.state = 'loading';
    btn.textContent = 'Adding\u2026';

    // Re-render the /cart page in place when the full cart is on screen.
    // PDP/collection have no <cart-items>, so they keep the drawer confirmation.
    var cartItems = document.querySelector('cart-items');
    var renderInPlace =
      cartItems &&
      typeof cartItems.getSectionsToRender === 'function' &&
      typeof cartItems.getSectionInnerHTML === 'function';

    var addBody = { items: [{ id: Number(variantId), quantity: 1 }] };
    // Shopify standard event (Digital #97, assets/rs-cart-events.js; looked up
    // now because this file runs before that deferred one). Null when missing.
    var ce = window.rsCartEvents;
    var lines = ce ? ce.linesUpdate(btn, 'add', 'product', [{ merchandiseId: Number(variantId), quantity: 1 }]) : null;
    if (renderInPlace) {
      addBody.sections = cartItems.getSectionsToRender().map(function (s) { return s.section; });
      addBody.sections_url = window.location.pathname;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(addBody)
    })
    .then(function (res) {
      if (!res.ok) return res.json().then(function (d) { throw new Error(d.description || ('HTTP ' + res.status)); });
      return res.json();
    })
    .then(function (parsedState) {
      btn.dataset.state = 'added';
      btn.textContent = 'Added';

      // /cart/add.js has no cart totals: one shared /cart.js read resolves the
      // event; the badge update below and the drawer (rsOpenDrawer, 500ms on)
      // reuse it.
      if (ce) ce.mutated();
      var cartRead = ce ? ce.fetchCart() : null;
      if (lines) cartRead.then(function (cart) { lines.resolve(cart); });

      // In-place 4-section swap (line items, footer/Order Summary, icon bubble, live region).
      // Mirrors cart.js updateQuantity's render loop, driven via the element's public methods.
      var didRenderInPlace = false;
      if (renderInPlace && parsedState && parsedState.sections) {
        try {
          cartItems.getSectionsToRender().forEach(function (section) {
            var html = parsedState.sections[section.section];
            if (html == null) return;
            var host = document.getElementById(section.id);
            if (!host) return;
            var target = host.querySelector(section.selector) || host;
            target.innerHTML = cartItems.getSectionInnerHTML(html, section.selector);
          });
          if (typeof parsedState.item_count === 'number') {
            cartItems.classList.toggle('is-empty', parsedState.item_count === 0);
            var footer = document.getElementById('main-cart-footer');
            if (footer) footer.classList.toggle('is-empty', parsedState.item_count === 0);
          }
          didRenderInPlace = true;
          // Keep the header bag badge in sync — the in-place swap repaints the
          // /cart sections but not the drawer's #rs-badge. cart-popup.js's
          // badge-repaint subscriber reads cartData.item_count.
          if (typeof window.rsPublishCartUpdate === 'function') {
            // /cart/add.js returns {items, sections} with no top-level item_count,
            // which the badge subscriber needs. Fetch the authoritative cart count.
            (cartRead || fetch('/cart.js').then(function (r) { return r.json(); }))
              .then(function (cart) {
                if (!cart) return;
                window.rsPublishCartUpdate({ source: 'rs-quick-add', cartData: cart });
              })
              .catch(function () { /* badge stays as-is; non-fatal */ });
          }
        } catch (e) {
          console.error('rs-quick-add: in-place cart render failed, falling back to drawer', e);
          didRenderInPlace = false;
        }
      }

      // Open the drawer ONLY when we did not re-render the page in place.
      if (!didRenderInPlace) {
        setTimeout(function () {
          if (typeof window.rsOpenDrawer === 'function') window.rsOpenDrawer('cart');
        }, 500);
      }

      setTimeout(function () {
        btn.dataset.state = '';
        btn.textContent = restLabel();
        if (picker && outerBtn) closePicker(picker, outerBtn);
      }, 2000);
    })
    .catch(function (err) {
      if (lines) lines.reject(err);
      if (ce) ce.cartError(btn, err && err.message, 'INVALID');
      console.error('rs-quick-add: cart add failed', err);
      btn.dataset.state = '';
      btn.textContent = "Couldn\u2019t add \u2014 try again";
      setTimeout(function () { btn.textContent = restLabel(); }, 3000);
    });
  }

  // ── Init ─────────────────────────────────────────────────────────────────────
  // Wires .rs-pc__quick-add buttons found inside `root` (defaults to document).
  // Idempotent via data-rs-wired guard — safe to re-invoke for dynamically
  // injected cards (e.g., rs-customers-also-bought fetching post-DCL).
  function wireQuickAdd(root) {
    root = root || document;
    var buttons = root.querySelectorAll('.rs-pc__quick-add');
    if (!buttons.length) return;

    buttons.forEach(function (btn) {
      if (btn.dataset.rsWired === 'true') return;
      btn.dataset.rsWired = 'true';

      // Scope picker + variant-JSON lookup to the button's host card so two
      // consumers rendering the same product on one page don't cross-wire.
      // Same pattern as findPriceNode (closest-ancestor + host.querySelector).
      var host = btn.closest('.rs-pc__card, [data-rs-qa-host]');
      if (!host) return;
      var productId = btn.dataset.productId;
      var picker = host.querySelector('.rs-pc__picker');
      var variants = parseVariants(host, productId);
      if (!variants || !variants.length) return;

      // ── Single-variant: direct add (no picker) ─────────────────────────────
      if (variants.length === 1) {
        if (!variants[0].available) {
          btn.textContent = 'Sold Out';
          btn.disabled = true;
        } else {
          btn.addEventListener('click', function () {
            addToCart(variants[0].id, btn);
          });
        }
        return;
      }

      // ── Multi-variant: picker toggle ───────────────────────────────────────
      if (!picker) return;

      btn.addEventListener('click', function () {
        if (picker.dataset.open === 'true') {
          closePicker(picker, btn);
        } else {
          openPicker(picker, btn, variants);
        }
      });

      // Swatch / size selection + Add to Cart inside picker
      picker.addEventListener('click', function (e) {
        var selBtn = e.target.closest('.rs-pc__picker-swatch, .rs-pc__picker-size');
        if (selBtn && selBtn.dataset.unavailable !== 'true') {
          var row = selBtn.closest('.rs-pc__picker-row');
          row.querySelectorAll('[data-value]').forEach(function (s) {
            s.dataset.selected = 'false';
            if (s.classList.contains('rs-pc__picker-size')) {
              s.classList.remove('rs-label-inverse');
            }
          });
          selBtn.dataset.selected = 'true';
          if (selBtn.classList.contains('rs-pc__picker-size')) {
            selBtn.classList.add('rs-label-inverse');
          }
          if (selBtn.classList.contains('rs-pc__picker-swatch')) {
            updateColorLabel(picker);
            swapCardImage(picker, selBtn.dataset.value);
          }
          updateUnavailability(picker, variants);
          updateCardPrice(picker, variants);
        }

        var addBtn = e.target.closest('.rs-pc__picker-add');
        if (addBtn && addBtn.dataset.state !== 'loading') {
          var variant = getSelectedVariant(picker, variants);
          if (variant && variant.available) {
            addToCart(variant.id, addBtn, picker, btn);
          } else {
            // Restore from the selection, not a saved string: a second click
            // inside the 2 s used to save 'Unavailable' itself and leave it.
            addBtn.textContent = 'Unavailable';
            setTimeout(function () { paintAddBtn(addBtn); }, 2000);
          }
        }
      });
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    wireQuickAdd(document);
  });

  // Dynamic-card injection hook.
  // rs-customers-also-bought.js dispatches this after fetching & appending cards.
  document.addEventListener('rs:cards-added', function (e) {
    var root = (e.detail && e.detail.root) || document;
    wireQuickAdd(root);
  });

})();
